/* eslint-disable i18next/no-literal-string */
import { Box, Divider, Theme, Typography } from "@outschool/backpack";
import {
  ActivityEnrollableTimesModal_ActivityFragment,
  ClassDetailsNoSectionsQuery,
  ClassDetailsQuery_ActivityFragment,
} from "@outschool/gql-frontend-generated";
import {
  faBirthdayCake,
  faCalendarAlt,
  faCheck,
  faClock,
  faSignalStream,
  faUsers,
  faVideoSlash,
} from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { useTrackEvent } from "@outschool/ui-analytics";
import { useSession } from "@outschool/ui-auth";
import { TrackedButton } from "@outschool/ui-components-shared";
import { useScrollToElement } from "@outschool/ui-utils";
import React, { SetStateAction, useCallback, useMemo } from "react";

import usePriceDetailsString from "../hooks/usePriceDetailsString";
import { SelfPacedIconBullets } from "../icons";
import { IconBullet } from "../PriceDetailsComponents";
import { SaveButton } from "../Saving";
import { useShowCredits } from "../Subscriptions";
import useActivityString from "../translations/useActivityString";
import ActivityShareMenu from "./ActivityShareMenu";
import {
  ChooseATimeOrClassScheduleRequestButtonProps,
  SectionCallToActionProps,
} from "./AvailableTimes/types";
import { AskAQuestionButtonMemo } from "./ClassDetailsMainBody/Buttons";
import PriceCreditsLineItem from "./PriceCreditsLineItem";
import PriceLineItem from "./PriceLineItem";

interface DesktopActivityCardProps {
  activity: ClassDetailsQuery_ActivityFragment;
  activityWithEnrollableTimes?: ActivityEnrollableTimesModal_ActivityFragment;
  leader: ClassDetailsNoSectionsQuery["activity"]["leader"];
  isOrganizationEnrollment: boolean;
  isLoadingOneOnOneAvailabilities?: boolean;
  hasOneOnOneAvailabilities: boolean;
  isReadOnly?: boolean;
  onClickViewReviews?: (event: React.SyntheticEvent) => void;
  onClickMoreLikeThis?: () => void;
  sectionsRef?: React.RefObject<HTMLElement>;
  viewAvailabilityRef?: React.RefObject<HTMLElement>;
  SectionCallToAction?: React.FC<SectionCallToActionProps>;
  ChooseATimeOrClassScheduleRequestButton?: (
    props: ChooseATimeOrClassScheduleRequestButtonProps
  ) => JSX.Element;
  isActivitySavedByCurrentUser?: boolean;
  facebookAppId?: string;
  setIsAvailabilityModalOpen?: React.Dispatch<SetStateAction<boolean>>;
  TutorEnrollButton?: React.ElementType;
  MemberPlanDiscountTooltip?: React.JSX.Element;
  MessageModal?: React.ElementType;
}

export default function DesktopActivityCard({
  activity,
  activityWithEnrollableTimes,
  leader,
  isOrganizationEnrollment,
  isLoadingOneOnOneAvailabilities,
  hasOneOnOneAvailabilities,
  isReadOnly,
  onClickMoreLikeThis,
  sectionsRef,
  viewAvailabilityRef,
  SectionCallToAction,
  ChooseATimeOrClassScheduleRequestButton,
  isActivitySavedByCurrentUser,
  facebookAppId,
  setIsAvailabilityModalOpen,
  TutorEnrollButton,
  MemberPlanDiscountTooltip,
  MessageModal,
}: DesktopActivityCardProps) {
  const { currentUser, isLoggedIn } = useSession();
  const { t } = useTranslation(
    "ui-components-website\\ClassDetailsPage\\DesktopActivityCard"
  );
  const trackEvent = useTrackEvent();
  const showCredits = useShowCredits();

  const { ageRangeString, classSizeString } = useActivityString();
  const {
    classDurationShortString,
    classFormatString,
    weeklyMeetingsSearchString,
    videoMeetingsString,
  } = usePriceDetailsString();

  const seeAvailableTimesButtonRef = React.useRef<HTMLButtonElement>(null);
  const scrollToElement = useScrollToElement({ desktopOffset: 80 });
  const onClickViewGroupClassAvailability = useCallback(() => {
    if (sectionsRef?.current) {
      scrollToElement(sectionsRef.current);
      seeAvailableTimesButtonRef.current?.blur();
    } else if (setIsAvailabilityModalOpen) {
      setIsAvailabilityModalOpen(true);
    }
  }, [sectionsRef, scrollToElement, setIsAvailabilityModalOpen]);
  const onClickViewTutoringAvailability = useCallback(() => {
    if (viewAvailabilityRef?.current) {
      scrollToElement(viewAvailabilityRef.current);
    } else if (setIsAvailabilityModalOpen) {
      setIsAvailabilityModalOpen(true);
    }
  }, [viewAvailabilityRef, scrollToElement, setIsAvailabilityModalOpen]);

  const callToActionMemo = useMemo(() => {
    if (isReadOnly) {
      return null;
    }

    const size = "large";
    // on-demand flex
    if (activity.isSelfPaced && SectionCallToAction) {
      return (
        <SectionCallToAction
          activity={activity}
          section={{}}
          variant={"contained"}
          isOrganizationEnrollment={isOrganizationEnrollment}
          sx={{ width: "100%" }}
          trackingSharedProperties={{
            component: "DesktopActivityCard",
          }}
          size={size}
        />
      );
    }

    if (activity.wasPublishedInThePast) {
      return (
        <>
          <Typography
            variant="subtitle1"
            sx={{ color: "error.600", marginBottom: "1em" }}
          >
            {activity.wasPublishedInThePast
              ? t`This class is no longer offered`
              : t`This class has no upcoming meetings`}
          </Typography>
          <TrackedButton
            variant="contained"
            onClick={onClickMoreLikeThis}
            sx={{ width: "100%" }}
            trackingName="cdp_see_more_like_this"
            size={size}
          >
            {t`See More Like This`}
          </TrackedButton>
        </>
      );
    }
    // tutoring
    if (activity.hasTeacherSchedule) {
      if (leader.hasBackgroundCheckExpired && !leader.ownerOfSellerOrg) {
        return (
          <>
            <Typography
              variant="subtitle1"
              sx={{ color: "error.600", marginBottom: "1em" }}
            >
              {t`This class is temporarily unavailable`}
            </Typography>
            {onClickMoreLikeThis && (
              <TrackedButton
                variant="contained"
                onClick={onClickMoreLikeThis}
                sx={{ width: "100%" }}
                trackingName="cdp_see_more_like_this"
                size={size}
              >
                {t`See More Like This`}
              </TrackedButton>
            )}
          </>
        );
      }
      if (hasOneOnOneAvailabilities || isLoadingOneOnOneAvailabilities) {
        if (TutorEnrollButton) {
          return (
            <TutorEnrollButton
              activity={activity}
              buttonText={t("See All Available Times")}
              activityWithEnrollableTimes={activityWithEnrollableTimes}
            />
          );
        }
        return (
          <TrackedButton
            variant={"contained"}
            onClick={onClickViewTutoringAvailability}
            sx={{ width: "100%" }}
            trackingName="cdp_see_available_times_tutoring"
            trackingUniqueId={activity.uid}
            size={size}
          >
            {t`See All Available Times`}
          </TrackedButton>
        );
      }
      return ChooseATimeOrClassScheduleRequestButton ? (
        <>
          <Typography
            variant="subtitle1"
            sx={{ color: "error.600", marginBottom: "1em" }}
          >
            {t`This class is sold out`}
          </Typography>
          <ChooseATimeOrClassScheduleRequestButton
            activity={activity}
            leader={leader}
            variant="contained"
            sx={{ width: "100%" }}
            /* eslint-disable-next-line i18next/no-literal-string */
            locationRenderedOn="DesktopHeaderCallout"
          />
        </>
      ) : null; // null case not expected but required for TS
    }

    // chat group
    if (activity.isClub && SectionCallToAction) {
      return (
        <SectionCallToAction
          activity={activity}
          section={activity.clubSection}
          isOrganizationEnrollment={isOrganizationEnrollment}
          trackingSharedProperties={{
            component: "DesktopActivityCard",
          }}
          sx={{ width: "100%" }}
          size={size}
        />
      );
    }

    // group class
    if (
      activity.canEnroll ||
      activity.allows_late_enrollments_for_fixed_length_classes
    ) {
      return (
        <TrackedButton
          ref={seeAvailableTimesButtonRef}
          variant={"contained"}
          onClick={onClickViewGroupClassAvailability}
          sx={{ width: "100%" }}
          trackingName="cdp_see_available_times_group"
          trackingUniqueId={activity.uid}
          size={size}
        >
          {t`See All Available Times`}
        </TrackedButton>
      );
    }

    return ChooseATimeOrClassScheduleRequestButton ? (
      <>
        {activity.isSoldOut && !activity.allowWaitlist && (
          <Typography
            variant="subtitle1"
            sx={{ color: "error.600", marginBottom: "1em" }}
          >
            {t`This class is sold out`}
          </Typography>
        )}
        <ChooseATimeOrClassScheduleRequestButton
          activity={activity}
          leader={leader}
          variant="contained"
          sx={{ width: "100%" }}
          /* eslint-disable-next-line i18next/no-literal-string */
          locationRenderedOn="DesktopHeaderCallout"
        />
      </>
    ) : null; // null case not expected but required for TS
  }, [
    activity,
    leader,
    isReadOnly,
    isLoadingOneOnOneAvailabilities,
    hasOneOnOneAvailabilities,
    isOrganizationEnrollment,
    onClickViewTutoringAvailability,
    onClickViewGroupClassAvailability,
    onClickMoreLikeThis,
    ChooseATimeOrClassScheduleRequestButton,
    SectionCallToAction,
    t,
    TutorEnrollButton,
    activityWithEnrollableTimes,
  ]);

  const saveButtonMemo = useMemo(() => {
    return (
      <SaveButton
        variant="link"
        isSet={isActivitySavedByCurrentUser}
        activity={activity}
        disabled={false}
        sx={{}}
        simpleHeart
      />
    );
  }, [activity, isActivitySavedByCurrentUser]);

  const activityShareMenuMemo = useMemo(() => {
    return (
      <ActivityShareMenu
        user={currentUser}
        activity={activity}
        facebookAppId={facebookAppId}
        simpleShare
      />
    );
  }, [currentUser, activity, facebookAppId]);

  return (
    <Box
      sx={(theme: Theme) => ({
        flexShrink: 0,
        width: "310px",
        border: "1px solid",
        borderColor: "neutral.300",
        borderRadius: "2em",
        borderTopLeftRadius: 16,
        boxShadow: "0px 4px 8px 0px #0000000F",
        [theme.breakpoints.down("lg")]: {
          width: "290px",
        },
      })}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: "primary.50",
          padding: 16,
          paddingRight: 12,
          borderTopLeftRadius: 16,
          borderTopRightRadius: 32,
          height: 62,
        }}
      >
        <Typography variant="h6" emphasized>
          {classFormatString(activity)}
        </Typography>
        <Box sx={{ display: "flex", gap: 8 }}>
          {saveButtonMemo}
          {activityShareMenuMemo}
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "0.25em",
          margin: "1em",
        }}
      >
        {showCredits ? (
          <>
            <PriceCreditsLineItem activity={activity} />
            {MemberPlanDiscountTooltip}
          </>
        ) : (
          <PriceLineItem activity={activity} />
        )}
      </Box>
      <Divider sx={{ borderColor: "neutral.200" }} />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "0.25em",
          margin: "1em",
        }}
      >
        {activity.isSelfPaced ? (
          <SelfPacedIconBullets activity={activity} />
        ) : (
          <>
            <IconBullet
              icon={faCalendarAlt}
              text={weeklyMeetingsSearchString(activity)}
            />
            {!activity.isClub && (
              <IconBullet
                icon={faClock}
                text={classDurationShortString(activity)}
              />
            )}
          </>
        )}
        {activity.completedEnrollmentCount > 0 && (
          <IconBullet
            icon={faCheck}
            iconColor="info"
            text={t("Completed by {{count}} learner", {
              count: activity.completedEnrollmentCount,
              defaultValue_plural: "Completed by {{count}} learners",
            })}
          />
        )}
        {activity.isClub && (
          <IconBullet
            icon={faCheck}
            iconColor="info"
            text={t("{{count}} active learner", {
              count: activity.clubSection?.validEnrollmentCount ?? 0,
              defaultValue_plural: "{{count}} active learners",
            })}
          />
        )}
        {!activity.isSelfPaced && (
          <IconBullet
            icon={
              !activity.isFlexSchedule && !activity.isClub
                ? faSignalStream
                : faVideoSlash
            }
            text={videoMeetingsString(activity)}
          />
        )}
        <IconBullet icon={faBirthdayCake} text={ageRangeString(activity)} />
        {!activity.isClub &&
          !activity.isFlexSchedule &&
          activity.size_max != 1 && (
            <IconBullet
              icon={faUsers}
              text={t("{{learnerCount}} learners per class", {
                learnerCount: classSizeString(activity),
              })}
            />
          )}
      </Box>
      {!isReadOnly && SectionCallToAction && (
        <>
          <Divider sx={{ borderColor: "neutral.200" }} />
          <Box sx={{ margin: "1em" }}>
            {callToActionMemo && (
              <Box
                sx={{
                  marginTop: "1em",
                  marginBottom: "0.25em",
                  textAlign: "center",
                }}
                data-testid="card-cta"
              >
                {callToActionMemo}
              </Box>
            )}
            {isLoggedIn && !isReadOnly && !!MessageModal && (
              <Box sx={{ marginTop: "0.5em" }}>
                <AskAQuestionButtonMemo
                  activity={activity}
                  currentUser={currentUser}
                  isLoggedIn={isLoggedIn}
                  leader={leader}
                  trackEvent={trackEvent}
                  MessageModal={MessageModal}
                />
              </Box>
            )}
          </Box>
        </>
      )}
    </Box>
  );
}
